import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';

type KeyStatus = 'active' | 'revoked';
type KeyInfo = {
  id: number;
  prefix: string;
  last4: string;
  status: KeyStatus;
  scopes: string[];
  lastUsedAt?: string | null;
};

interface Props {
  token: string;
}


const Settings: React.FC<Props> = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState<KeyInfo | null>(null);
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchKey = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await api.get(`/apikey`, auth);
      setKey(res.data?.key || null);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load API key');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKey(); // eslint-disable-next-line
  }, []);

  const generateKey = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await api.post(`/apikey`, {}, auth);
      setRawKey(res.data?.apiKey || null); // shown once
      await fetchKey();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Could not generate key');
    } finally {
      setBusy(false);
    }
  };

  const revokeKey = async () => {
    if (!key) return;
    if (!window.confirm('Revoke this API key? Existing integrations will stop working.')) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/apikey/${key.id}/revoke`, {}, auth);
      setRawKey(null);
      await fetchKey();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Could not revoke key');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!rawKey) return;
    try {
      await navigator.clipboard.writeText(rawKey);
      alert('Copied!');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = rawKey; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
      alert('Copied!');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold mb-6">Settings</h1>

      {/* API Key Section */}
      <section className="mb-8">
        <h2 className="text-lg font-medium mb-3">Developer / API Access</h2>

        {loading ? (
          <div>Loading…</div>
        ) : (
          <div className="space-y-6">
            {/* One-time raw key reveal */}
            {rawKey && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <div className="font-medium mb-1">Your new API key (shown once):</div>
                <div className="font-mono text-sm break-all p-2 bg-white rounded border">{rawKey}</div>
                <div className="text-xs text-gray-600 mt-2">
                  Store this key securely. You won’t be able to see it again.
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={copy} className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">
                    Copy
                  </button>
                  <button onClick={() => setRawKey(null)} className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300">
                    Hide
                  </button>
                </div>
              </div>
            )}

            {/* Current key status */}
            <div className="border rounded-lg p-4">
              <div className="font-medium mb-3">Current Key</div>
              {!key ? (
                <div className="text-sm text-gray-600">No key created yet.</div>
              ) : (
                <div className="text-sm">
                  <div><span className="text-gray-600">Mask:</span> {key.prefix}…{key.last4}</div>
                  <div><span className="text-gray-600">Status:</span> {key.status}</div>
                  <div><span className="text-gray-600">Scopes:</span> {key.scopes?.join(', ') || '—'}</div>
                  {key.lastUsedAt && (
                    <div><span className="text-gray-600">Last used:</span> {new Date(key.lastUsedAt).toLocaleString()}</div>
                  )}
                </div>
              )}

              <div className="mt-4 flex gap-2">
                {!key || key.status === 'revoked' ? (
                  <button
                    onClick={generateKey}
                    disabled={busy}
                    className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                  >
                    Generate API Key
                  </button>
                ) : (
                  <button
                    onClick={revokeKey}
                    disabled={busy}
                    className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    Revoke Key
                  </button>
                )}
              </div>

              {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
            </div>

            {/* Usage help */}
            <div className="border rounded-lg p-4">
              <div className="font-medium mb-2">How to use</div>
              <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto">
                {`# Call RAG with API Key (no JWT needed)
curl -X POST /ai/query \
  -H "x-api-key: <your_api_key>" \
  -H "Content-Type: application/json" \
  -d '{"prompt":"Hello","kb_id":1}'`}
              </pre>
              <div className="text-xs text-gray-600 mt-2">
                Or send <code>Authorization: ApiKey &lt;your_api_key&gt;</code>. Rate limits apply.
              </div>
            </div>
          </div>
        )}
      </section>

      {/* (Optional) add more settings sections here: password change, profile, etc. */}
    </div>
  );
};

export default Settings;
